import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  CountryRecyclability,
  PackageType,
  ProvincialRecyclability,
  RecyclabilityCountry,
  RecyclabilityFormData,
} from '../../../types/recyclability'
import { Dialog } from '@angular/cdk/dialog'
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { DialogService } from '../../../services/dialog.service'
import { RouteService } from '../../../services/route.service'
import { TranslationService } from '../../../services/translation.service'
import { MatRadioModule } from '@angular/material/radio'
import { MatButtonModule } from '@angular/material/button'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatIconModule } from '@angular/material/icon'
import { CANADIAN_PROVINCES } from '../../../constants/provinces'
import { CommonModule, NgOptimizedImage } from '@angular/common'
import { ProvinceRecyclabilityInfoComponent } from './province-recyclability-info/province-recyclability-info.component'
import { MatDivider } from '@angular/material/divider'
import { CountryRecyclabilityInfoComponent } from './country-recyclability-info/country-recyclability-info.component'
import {
  RECYCLABILITY_COUNTRIES,
  RECYCLABILITY_COUNTRY_OPTIONS,
} from '../../../constants/country-recyclability'

@Component({
  selector: 'app-product-recyclability-form',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormField,
    MatRadioModule,
    MatFormField,
    MatLabel,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    CommonModule,
    ProvinceRecyclabilityInfoComponent,
    MatDivider,
    NgOptimizedImage,
    CountryRecyclabilityInfoComponent,
  ],
  templateUrl: './product-recyclability-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-recyclability-form.component.scss',
})
export class ProductRecyclabilityFormComponent implements OnChanges {
  private fb = inject(FormBuilder)
  private routeService = inject(RouteService)
  translationService = inject(TranslationService)
  private dialogService = inject(DialogService)
  private dialog = inject(Dialog)

  @Input() clonedData?: RecyclabilityFormData
  @Output() formChanged = new EventEmitter<RecyclabilityFormData>()
  @Output() testing = new EventEmitter<number>()
  @Output() backPressed = new EventEmitter()
  @Output() isPlasticPackaging = new EventEmitter<boolean>()

  constructor() {
    // Watch for changes in 'isPackage' to apply conditional validation
    this.recyclabilityForm
      .get('selectedProvinces')
      ?.valueChanges.subscribe((selectedProvinces) => {
        this.updateRecyclabilityFormArray(selectedProvinces ?? [])
      })

    this.recyclabilityForm
      .get('isPlasticPackaging')
      ?.valueChanges.subscribe((isPlasticPackaging) => {
        this.isPlasticPackaging.emit(isPlasticPackaging ?? false)
      })
  }

  provincesControl = new FormControl<string[]>([])

  recyclabilityForm = this.fb.group({
    isRecyclable: [false, Validators.required],
    recycledContentPercentage: [0],
    isPackage: [false, Validators.required],
    isPlasticPackaging: [false],
    packageType: [''],
    selectedProvinces: this.provincesControl,
    provincialRecyclability: this.fb.array([]),
    countryRecyclability: this.fb.array([]),
  })

  packageTypes: Array<PackageType> = [
    {
      name: 'Primary',
      translatedName: this.translationService.getPrimaryText(),
    },
    {
      name: 'Secondary',
      translatedName: this.translationService.getSecondaryText(),
    },
    {
      name: 'Tertiary',
      translatedName: this.translationService.getTertiaryText(),
    },
  ]

  provinces = CANADIAN_PROVINCES
  isFormSubmitted = false

  countries = RECYCLABILITY_COUNTRIES
  selectedCountries: Array<RecyclabilityCountry> = []
  canadaIsSelected = false

  ngOnChanges(changes: SimpleChanges) {
    if (changes['clonedData'] && changes['clonedData'].currentValue) {
      this.populateFormWithData(changes['clonedData'].currentValue)
    }
  }

  /**
   * Populates the recyclability form with previously saved data.
   *
   * @param {RecyclabilityFormData} data - The recyclability form data object
   * @param {boolean} data.isRecyclable - Indicates if the item is recyclable
   * @param {number} data.recycledContentPercentage - The percentage of recycled content in the item (required if isRecyclable is true)
   * @param {boolean} data.isPackage - Indicates if the item is a package
   * @param {boolean} data.isPlasticPackaging - Indicates that the item is plastic packaging
   * @param {string} data.packageType - The type of package
   * @param {Array<ProvincialRecyclability>} data.provincialRecyclability - Array of provincial recyclability data
   * @param {string} data.provincialRecyclability[].province - The province code/name from CANADIAN_PROVINCES
   * @param {string} data.provincialRecyclability[].recyclabilityStatus - The recyclability status for the province
   *
   *
   */
  private populateFormWithData(data: RecyclabilityFormData) {
    // Pre-populate form with data
    this.recyclabilityForm.patchValue({
      isRecyclable: data?.isRecyclable,
      recycledContentPercentage: data?.recycledContentPercentage,
      isPackage: data?.isPackage,
      isPlasticPackaging: data?.isPlasticPackaging ?? false,
      packageType: data?.packageType,
    })

    this.isPlasticPackaging.emit(data?.isPlasticPackaging ?? false)

    // set the countries used by country-level recyclability (e.g. Hong Kong)
    this.selectedCountries =
      data?.countryRecyclability?.map((recyclability) => {
        return recyclability.country
      }) ?? []

    const hasProvincialRecyclability =
      (data?.provincialRecyclability?.length ?? 0) > 0

    // Canada is derived from provincial recyclability, not country recyclability
    if (hasProvincialRecyclability) {
      this.selectedCountries.push(RECYCLABILITY_COUNTRY_OPTIONS.CANADA)
    }

    // initialize country/province UI state after selectedCountries is prepared
    this.canadaIsSelected = hasProvincialRecyclability
    this.toggleCanadianProvinceValidators()

    // populate country recyclability data
    if (data?.countryRecyclability?.length) {
      // trigger country change to have controls initialized
      this.onCountryChange()

      // set control values
      const countryRecyclabilityFormArray = this.recyclabilityForm.get(
        'countryRecyclability',
      ) as FormArray

      data.countryRecyclability.forEach((cr, index) => {
        countryRecyclabilityFormArray.at(index).patchValue({
          country: cr.country,
          recyclabilityStatus: cr.recyclabilityStatus,
        })
      })
    }

    // Pre-populate provincial recyclability data
    if (data?.provincialRecyclability?.length > 0) {
      const provinces = data.provincialRecyclability.map((pr) => pr.province)
      this.recyclabilityForm.get('selectedProvinces')?.patchValue(provinces)

      // This will trigger the creation of form controls for each province
      this.updateRecyclabilityFormArray(provinces)

      // Set the recyclability status for each province
      const recyclabilityFormArray = this.recyclabilityForm.get(
        'provincialRecyclability',
      ) as FormArray

      data.provincialRecyclability.forEach((pr, index) => {
        recyclabilityFormArray.at(index).patchValue({
          province: pr.province,
          recyclabilityStatus: pr.recyclabilityStatus?.toLowerCase(),
        })
      })
    }
  }

  /**
   * Toggles validators for Canadian province controls based on country selection
   */
  toggleCanadianProvinceValidators() {
    const selectedProvincesControl =
      this.recyclabilityForm.get('selectedProvinces')

    // if Canada is selected as a country, we need at least one province to be selected.
    if (this.canadaIsSelected) {
      selectedProvincesControl?.setValidators([
        Validators.required,
        this.atLeastOneSelected,
      ])
    } else {
      if (selectedProvincesControl) {
        selectedProvincesControl?.clearValidators()

        const recyclabilityFormArray = this.recyclabilityForm.get(
          'provincialRecyclability',
        )

        if (recyclabilityFormArray instanceof FormArray) {
          recyclabilityFormArray.clear()
          recyclabilityFormArray.setValue([])
        } else {
          console.error('provincialRecyclability is not a FormArray')
        }

        selectedProvincesControl?.setValue([])
      }
    }

    if (selectedProvincesControl) {
      // Update validity after changing validators
      selectedProvincesControl?.updateValueAndValidity()
      selectedProvincesControl?.markAsUntouched()
    }
  }

  /**
   * Emits form data
   */
  async onSubmit() {
    this.isFormSubmitted = true
    this.recyclabilityForm.markAllAsTouched()
    if (!this.recyclabilityForm.valid) {
      return
    }

    this.formChanged.emit({
      formName: 'recyclabilityForm',
      isRecyclable: this.recyclabilityForm.get('isRecyclable')?.value ?? false,
      recycledContentPercentage:
        this.recyclabilityForm.get('recycledContentPercentage')?.value ?? 0,
      isPackage: this.recyclabilityForm.get('isPackage')?.value ?? false,
      isPlasticPackaging:
        this.recyclabilityForm.get('isPlasticPackaging')?.value ?? false,
      packageType: this.recyclabilityForm.get('packageType')?.value ?? '',
      provincialRecyclability:
        (this.recyclabilityForm.get('provincialRecyclability')
          ?.value as Array<ProvincialRecyclability>) ?? [],
      countryRecyclability:
        (this.recyclabilityForm.get('countryRecyclability')
          ?.value as Array<CountryRecyclability>) ?? [],
    })
  }

  /**
   * Resets the form
   */
  reset() {
    this.recyclabilityForm.reset({
      isRecyclable: false,
      recycledContentPercentage: 0,
      isPackage: false,
      isPlasticPackaging: false,
      packageType: '',
    })

    this.isPlasticPackaging.emit(false)

    this.recyclabilityForm.markAsPristine()
    this.recyclabilityForm.markAsUntouched()
    this.isFormSubmitted = false
  }

  /**
   * Handles the click of the back button to navigate to previous form
   */
  goBack() {
    this.backPressed.emit(null)
  }

  // Custom validator to check if at least one province is selected
  atLeastOneSelected(control: AbstractControl) {
    return control.value && control.value.length > 0
      ? null
      : { atLeastOneRequired: true }
  }

  // Getters for accessing form controls and validation state
  get selectedProvinces() {
    return this.recyclabilityForm.get('selectedProvinces')
  }

  // Check if form control is invalid
  get isProvinceSelectionInvalid() {
    return this.selectedProvinces?.touched && this.selectedProvinces?.invalid
  }

  get provincialRecyclabilityControls(): FormGroup[] {
    return (this.recyclabilityForm.get('provincialRecyclability') as FormArray)
      .controls as FormGroup[]
  }

  // Updates provincial recyclability form array when provinces are selected/unselected
  updateRecyclabilityFormArray(selectedProvinces: string[]) {
    this.isFormSubmitted = false

    const recyclabilityFormArray = this.recyclabilityForm.get(
      'provincialRecyclability',
    ) as FormArray

    // Track existing provinces in the form array
    const currentProvinces = recyclabilityFormArray.controls.map(
      (control) => control.get('province')?.value,
    )

    // Remove controls that are no longer in selectedProvinces
    currentProvinces.forEach((province, index) => {
      if (!selectedProvinces.includes(province)) {
        recyclabilityFormArray.removeAt(index)
      }
    })

    // Add controls for newly selected provinces
    selectedProvinces.forEach((province) => {
      if (!currentProvinces.includes(province)) {
        recyclabilityFormArray.push(
          this.fb.group({
            province: province,
            recyclabilityStatus: new FormControl('', Validators.required),
          }),
        )
      }
    })
  }

  // Handle country selection
  onCountryChange() {
    this.isFormSubmitted = false

    const countryRecyclabilityFormArray = this.recyclabilityForm.get(
      'countryRecyclability',
    ) as FormArray

    // Track existing countries in the form array
    const currentCountries = countryRecyclabilityFormArray.controls.map(
      (control) => control.get('country')?.value,
    )

    // Remove controls that are no longer in selectedCountries
    currentCountries.forEach((country, index) => {
      if (!this.recyclabilityCountriesSelected.includes(country)) {
        countryRecyclabilityFormArray.removeAt(index)
      }
    })

    // Add controls for newly selected countries
    this.recyclabilityCountriesSelected.forEach((country) => {
      if (!currentCountries.includes(country)) {
        countryRecyclabilityFormArray.push(
          this.fb.group({
            country: country,
            recyclabilityStatus: new FormControl('', Validators.required),
          }),
        )
      }
    })

    this.canadaIsSelected = this.selectedCountries.includes(
      RECYCLABILITY_COUNTRY_OPTIONS.CANADA,
    )
    this.toggleCanadianProvinceValidators()
  }

  get recyclabilityCountriesSelected() {
    return this.selectedCountries.filter((country) => country != 'Canada')
  }

  get countryRecyclabilityControls(): FormGroup[] {
    return (this.recyclabilityForm.get('countryRecyclability') as FormArray)
      .controls as FormGroup[]
  }
}
